import { useState, useCallback } from 'react'
import { formatDateTime, safeParseDate } from 'shared/util'
import { EditIcon, OkIcon, PlusIcon, TrushIcon } from 'shared/icons'

import { Alert, Box, Button, Divider, MenuItem, Select, Stack, StackProps, Tooltip, Typography } from '@mui/material'

import { Loading } from 'components/controls/loading'
import { ErrorBlock, ErrorInfo } from 'components/controls/errorInfo'

import { useAgreementManagementContainer } from './container'

import { AgreementForm, useAgreementForm } from 'features/agreement/form'
import { AgreementResult, usePostAgreement, usePostDraftState, useRemoveAgreement } from 'interface/agreement'
import { MarkdownPreview } from 'components/controls/markDownPreview'

type AgreementEditFormProps = {
  editTarget: AgreementResult | null
  onPosted: () => void
  onCancel: () => void
} & StackProps
const AgreementEditForm = ({
  editTarget,
  onPosted,
  onCancel,
  ...others
}: AgreementEditFormProps) => {
  const [postState, post] = usePostAgreement({ onPosted })
  const editForm = useAgreementForm({
    agreement: editTarget,
    onSubmit: post
  })
  return (
    <Stack direction="column" flexGrow={1} overflow="hidden" {...others}>
      {postState.posting && <Loading/>}

      <AgreementForm
        {...editForm}
      />
      <Divider/>
      {postState.error && <ErrorBlock error={postState.error}/>}
      <Stack direction="row" padding={1} spacing={1} justifyContent="flex-end">
        <Button
          onClick={editForm.submit}
          loading={postState.posting}
          variant="contained"
          startIcon={<OkIcon/>}
        >
          保存
        </Button>

        <Button
          onClick={onCancel}
          variant="outlined"
        >
          キャンセル
        </Button>
      </Stack>
    </Stack>
  )
}
export const ConfigureAgreementPage = () => {
  const {
    loading, error,
    currentVersion, setCurrentVersion, versionList,
		current, reload
  } = useAgreementManagementContainer()

	const [mode, setMode] = useState<"edit" | null>(null)
  const [editTarget, setEditTarget] = useState<AgreementResult | null>(null)

  const onPosted = useCallback(
    () => {
      setEditTarget(null)
      setMode(null)
      reload()
    },
    [reload, setMode, setEditTarget]
  )
  const onEditStart = useCallback(
    () => {
      setEditTarget(current ? {...current} : null)
      setMode("edit")
    },
    [current, setMode, setEditTarget]
  )
  const onEditCancel = useCallback(
    () => {
      setMode(null)
      setEditTarget(null)
    },
    [setMode, setEditTarget]
  )

  const onDraftStatePosted = useCallback(
    () => {
      reload()
    },
    [reload]
  )
  const [postDraftState, postDraft] = usePostDraftState({ onPosted: onDraftStatePosted })
  const setDraftState = useCallback(
    (isDraft: boolean) => {
      if (!current) return
      postDraft({
        version: current.version,
        form: {
          isDraft,
          updateTime: current.updateTime
        }
      })
    },
    [postDraft, current]
  )

  const onRemoved = useCallback(
    () => {
      reload()
    },
    [reload]
  )
  const [removeState, remove] = useRemoveAgreement({ onRemoved })
  const onRemove = useCallback(
    () => {
      if (!current) return
      remove(current.version)
    },
    [remove, current]
  )

  return (
    <>
      {(loading || removeState.posting) && <Loading />}

      <Stack direction="column" spacing={1}>
        {error && <ErrorInfo error={error}/>}
        {removeState.error && <ErrorInfo error={removeState.error}/>}
      </Stack>

      {(mode === 'edit') ? (
        <AgreementEditForm
          editTarget={editTarget}
          onPosted={onPosted}
          onCancel={onEditCancel}
        />
      ) : (
        <Stack direction="column" overflow="hidden">
          <Stack direction="row" padding={1} spacing={1}>
            {(versionList && versionList.length > 0) ? (
              <>
                <Select<number>
                  value={currentVersion ?? current?.version ?? 0}
                  onChange={(ev) => setCurrentVersion(ev.target.value)}
                  size="small"
                >
                  {versionList.map(({ version, isDraft, createTime }) => (
                    <MenuItem key={`agreement-${version}`} value={version}>
                      {isDraft ? '【非公開】' : '【公開中】'}
                      {formatDateTime(safeParseDate(createTime) ?? null, '/', ' ', ':')} 作成
                    </MenuItem>
                  ))}
                </Select>

                <Button
                  onClick={onEditStart}
                  variant="contained"
                  startIcon={<EditIcon/>}
                >
                  編集
                </Button>

                <Button
                  onClick={onRemove}
                  variant="contained"
                  color="error"
                  startIcon={<TrushIcon/>}
                >
                  削除
                </Button>
              </>
            ) : (
              <Button
                onClick={onEditStart}
                variant="contained"
                startIcon={<PlusIcon/>}
              >
                新規作成
              </Button>
            )}
          </Stack>

          <Divider/>

          {current ? (
            <Stack direction="column" padding={2} spacing={2} overflow="hidden">
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant='h5' component="h5" flexGrow={1}>{current.title}</Typography>
                {current.isDraft ? (
                  <Tooltip title="公開する">
                    <Button
                      variant="contained"
                      color="warning"
                      loading={postDraftState.posting}
                      onClick={() => setDraftState(false)}
                    >
                      非公開
                    </Button>
                  </Tooltip>
                ) : (
                  <Tooltip title="非公開にする">
                    <Button
                      variant="outlined"
                      color="success"
                      startIcon={<OkIcon/>}
                      loading={postDraftState.posting}
                      onClick={() => setDraftState(true)}
                    >
                      公開中
                    </Button>
                  </Tooltip>
                )}
              </Stack>
              {current.enableTime && (
                <Alert severity="info">
                  本規約は {formatDateTime(safeParseDate(current.enableTime) ?? null, '/', ' ', ':')} から適用されます
                </Alert>
              )}
              <MarkdownPreview value={current.content} padding={0} overflow="auto"/>
            </Stack>
          ) : (
            <Box padding={1}>
              <Alert severity="warning" sx={{ wordBreak: 'break-all' }}>
                利用規約が保存されていません
              </Alert>
            </Box>
          )}
        </Stack>
      )}
    </>
  )
}
